/**
 * Firebase, for signing in.
 *
 * The sign-in pages Google and Facebook send people through (/__/auth/…) are
 * served from the app's own address in production: vite.config forwards /__
 * to Firebase. So Google's screen says "to continue to grow.gardengameplan.com"
 * rather than the project's own confident-totem-444916-n8.firebaseapp.com,
 * which read like somebody else's site; and a redirect sign-in (the installed
 * app) comes back to the same site that started it, which Safari needs to
 * hand the result over at all.
 *
 * Only on the addresses that are set up for it (OWN_AUTH_HOSTS): each needs
 * https://<host>/__/auth/handler listed on the OAuth client in Google Cloud
 * and on the Facebook app. Anywhere else (localhost) signs in through the
 * project's own address, as before.
 *
 * Used by: shared/context/AuthContext (every sign-in, sign-out and the
 * auth-state listener) and shared/api/apiClient (auth.currentUser's ID token
 * on each request). Nothing else talks to Firebase; with VITE_USE_MOCK_AUTH
 * on, the app is initialised here but never signs in through it.
 *
 * Env vars: VITE_FIREBASE_API_KEY, VITE_FIREBASE_AUTH_DOMAIN and
 * VITE_FIREBASE_PROJECT_ID, through config/env-vars; all public in the bundle,
 * as Firebase intends.
 *
 * Surprising: the auth domain is decided once, at module load, from the
 * address the page was opened on, so the same build signs in through its own
 * address on grow.gardengameplan.com and through the project's elsewhere.
 * Only authentication is set up; there is no Firestore or Storage here
 * (photos live in DigitalOcean Spaces, behind the backend).
 *
 * @module firebase
 */

import { initializeApp } from 'firebase/app';
import { getAuth, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { debugLog } from '@/config/config';
import { FIREBASE_OWN_AUTH_HOSTS } from '@/config/tunables/shared';
import {
  VITE_FIREBASE_API_KEY,
  VITE_FIREBASE_AUTH_DOMAIN,
  VITE_FIREBASE_PROJECT_ID
} from './env-vars';

/** Addresses that serve Firebase's sign-in pages themselves (see above). */
const OWN_AUTH_HOSTS = new Set(FIREBASE_OWN_AUTH_HOSTS);

/**
 * Where the sign-in pages are: this address when it serves them, otherwise
 * the project's own.
 *
 * @returns The auth domain for Firebase's config.
 */
function authDomain(): string {
  if (typeof window !== 'undefined' && OWN_AUTH_HOSTS.has(window.location.hostname)) {
    return window.location.host;
  }
  return VITE_FIREBASE_AUTH_DOMAIN;
}

/** Just what authentication needs. */
const firebaseConfig = {
  apiKey: VITE_FIREBASE_API_KEY,
  authDomain: authDomain(),
  projectId: VITE_FIREBASE_PROJECT_ID
};

/** The one Firebase app, made at load. */
const app = initializeApp(firebaseConfig);

/** Firebase Authentication, for the whole app. */
export const auth = getAuth(app);

/*
 * Kept signed in across tabs and restarts. LOCAL is Firebase's default; it is
 * set anyway so nobody wonders, and a refusal (storage blocked) is reported
 * rather than left as an unhandled rejection.
 */

setPersistence(auth, browserLocalPersistence)
  .then(() => {
    debugLog('FirebaseInit', 'Firebase persistence set to LOCAL');
  })
  .catch((error) => {
    console.error('Error setting persistence:', error);
  });

/** The Firebase app itself, for anything needing more than auth. */
export default app;
